import React, { useMemo, useRef, useState, useEffect } from "react";

/* -------------------------------------------------------
   Vertex + WhatsApp Ads Demo (no external libs)
   - Left: Brand ad mock (parallax + shine + glow)
   - Right: WhatsApp-style chat with Meta AI badge
   - CTA triggers sequential, topic-related replies
--------------------------------------------------------*/

export default function Demo({
  sectionMinH = "80vh",
  mediaHeight = "260px",
  chatMinH = "300px",
}) {
  /* ------------ state ------------ */
  const [toast, setToast] = useState("");
  const [spin, setSpin] = useState({ btn: false, wa: false });
  const [chat, setChat] = useState([
    {
      side: "left",
      ai: true,
      text: "Hi 👋 I’m Meta AI. Thanks for checking our sale! Want to see our catalog?",
      meta: { tag: "META • AI" },
    },
  ]);

  // rotating sequences (WhatsApp Ads → catalog → payment → support)
  const sequences = [
    [
      "Great! Here are our best-sellers: 1) Shoes 2) Jackets 3) Bags.",
      "Reply with a number or tap a quick reply to see sizes, colors and price.",
    ],
    [
      "You can order right here in chat ✅",
      "We’ll send a secure payment link after you confirm your selection.",
    ],
    [
      "Need help deciding? I can compare items or loop in a sales agent.",
      "Type 'agent' anytime to connect to a human.",
    ],
    [
      "Weekend offer: extra 10% off at checkout 🎉",
      "Limited stock — grab yours now!",
    ],
  ];
  const [seqIndex, setSeqIndex] = useState(0);

  const timeoutsRef = useRef([]);
  const addTimeout = (fn, ms) => {
    const id = setTimeout(fn, ms);
    timeoutsRef.current.push(id);
  };
  useEffect(() => () => timeoutsRef.current.forEach(clearTimeout), []);

  /* ------------ CTA click ------------ */
  const onSend = (payload = "Yes, show me.") => {
    setSpin((s) => ({ ...s, btn: true, wa: true }));
    addTimeout(() => setSpin((s) => ({ ...s, btn: false })), 600);
    addTimeout(() => setSpin((s) => ({ ...s, wa: false })), 600);
    setToast("Opening WhatsApp conversation…");

    // user bubble
    setChat((c) => [...c, { side: "right", text: payload }]);

    // show typing bubble
    const now = Date.now();
    setChat((c) => [...c, { side: "left", typing: true, key: now }]);

    // send next AI sequence
    const next = sequences[seqIndex % sequences.length];
    addTimeout(() => {
      setChat((c) =>
        c
          .filter((b) => !b.typing)
          .concat(
            next.map((t) => ({
              side: "left",
              ai: true,
              text: t,
              meta: { tag: "META • AI" },
            }))
          )
      );
      setSeqIndex((i) => i + 1);
    }, 900);
  };

  const liveMsg = useMemo(() => (toast ? `${toast}.` : ""), [toast]);

  /* ------------ left card hover parallax ------------ */
  const cardRef = useRef(null);
  const onMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const rx = ((y - r.height / 2) / r.height) * -6;
    const ry = ((x - r.width / 2) / r.width) * 6;
    el.style.setProperty("--rx", `${rx}deg`);
    el.style.setProperty("--ry", `${ry}deg`);
    el.style.setProperty("--mx", `${x}px`);
    el.style.setProperty("--my", `${y}px`);
  };
  const onLeave = () => {
    const el = cardRef.current;
    if (!el) return;
    el.style.setProperty("--rx", `0deg`);
    el.style.setProperty("--ry", `0deg`);
  };

  /* ------------ styles ------------ */
  const styles = `
  :root, html, body { background:#ffffff; }
  .demo-wrap, .demo-wrap * { box-sizing: border-box; }
  :root{
    --brand:#0b66c3;        /* Vertex blue */
    --brand-700:#094f9a;
    --ink:#0f172a;          /* headings/text */
    --muted:#64748b;        /* secondary text */
    --border:#e6edf5;
    --focus:#60a5fa;
    --wa:#25D366;           /* WhatsApp accent (small badge) */
  }

  .demo-wrap{
    min-height: var(--section-min-h);
    max-width: 1450px;
    margin: 0 auto;
    padding: 56px 60px 72px;
    background: #ffffff;
    color: var(--ink);
    font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
  }

  .demo-head{ text-align:center; margin-bottom:28px; }
  .demo-title{ margin:0; font-size:2.5rem; line-height:1.12; font-weight:900; letter-spacing:.2px; }
  .demo-tag{ margin:12px auto 0; max-width:720px; font-size:1rem; color:var(--muted); }

  .demo-grid{ display:grid; gap:24px; grid-template-columns:1fr 1fr; align-items:stretch; }
  @media (max-width:1000px){ .demo-grid{ grid-template-columns:1fr; } }
  @media (max-width:700px){ .demo-wrap{ padding:40px 20px 56px; } }

  .demo-card{
    background:#fff; border:1px solid var(--border); border-radius:16px;
    box-shadow:0 1px 6px rgba(16,24,40,.04);
    padding:20px; display:flex; flex-direction:column; gap:14px; position:relative;
  }
  .demo-card-head{ font-weight:900; display:flex; align-items:center; justify-content:space-between; }

  /* -------- Left: Vertex ad mock ---------- */
  .vx-brand{
    display:flex; align-items:center; gap:10px;
    margin-top:-4px;
  }
  .vx-mark{
    width:34px; height:34px; border-radius:9px; display:grid; place-items:center;
    background:var(--brand); color:#fff; font-weight:900; letter-spacing:.2px;
    box-shadow:0 6px 18px rgba(11,102,195,.25);
  }
  .vx-name{ font-weight:900; color:var(--brand); letter-spacing:.2px; }
  .vx-sub{ color:var(--muted); font-size:.9rem; }

  .demo-media{
    height: var(--media-h);
    border-radius: 16px;
    background: #f6f9fc;
    position:relative; overflow:hidden;
    display:flex; align-items:center; justify-content:center;
  }
  .demo-media::before{
    content:"";
    position:absolute; width:560px; height:560px; border-radius:999px; filter:blur(8px);
    background: radial-gradient(60% 60% at 50% 40%, #b3e6ff77 0%, #c9f7e777 45%, transparent 70%);
    transform: translateY(20px);
  }

  .ad-card{
    position:relative;
    width:min(560px,94%);
    border:1px solid var(--border);
    border-radius:20px;
    background:#fff;
    box-shadow:0 16px 48px rgba(2,6,23,.08);
    padding:18px;
    transform: perspective(900px) rotateX(var(--rx,0deg)) rotateY(var(--ry,0deg));
    transition: transform .15s ease, box-shadow .2s ease, border-color .2s ease;
    overflow:hidden;
  }
  .ad-card:hover{ box-shadow:0 22px 64px rgba(2,6,23,.12); border-color:#d9e7fb; }
  .ad-card::after{           /* shine sweep */
    content:"";
    position:absolute; inset:-1px;
    background: linear-gradient(120deg, transparent 40%, rgba(255,255,255,.75) 50%, transparent 60%);
    transform: translateX(-160%);
    transition: transform .8s ease;
    pointer-events:none;
  }
  .ad-card:hover::after{ transform: translateX(160%); }

  .ad-row{ display:flex; align-items:center; gap:14px; }
  .ad-ico{
    width:60px; height:60px; border-radius:16px; display:grid; place-items:center;
    background:#f5f7fb; box-shadow: inset 0 0 0 1px var(--border); font-size:30px; color:#0f172a;
  }
  .ad-title{ font-weight:900; margin:0; }
  .ad-sub{ color:var(--muted); margin-top:2px; }

  .ad-pill{
    margin-left:auto;
    padding:8px 12px; border-radius:999px; font-weight:800; font-size:.9rem;
    background:#eaf2ff; color:var(--brand); letter-spacing:.1px;
    box-shadow:0 6px 16px rgba(11,102,195,.15);
  }

  /* CTA row */
  .demo-row{ margin-top:auto; display:flex; align-items:center; justify-content:space-between; gap:14px; }
  .demo-caption-title{ font-weight:900; }
  .demo-caption-sub{ color:var(--muted); font-size:.95rem; }

  /* Brand button (outline → hover fill + ripple) */
  .vx-btn{
    position: relative;
    display:inline-flex; align-items:center; gap:8px;
    padding: 10px 16px;
    border-radius: 12px;
    background: #ffffff;
    color: var(--brand);
    border: 1.8px solid var(--brand);
    font-weight: 900;
    letter-spacing:.2px;
    transition: transform .12s ease, box-shadow .2s ease, background .2s ease, color .2s ease;
    outline: none;
    overflow: hidden;
    cursor:pointer;
  }
  .vx-btn:hover{
    background: var(--brand);
    color:#fff;
    box-shadow: 0 10px 24px rgba(11,102,195,.25);
    transform: translateY(-1px);
  }
  .vx-btn:active{ transform: translateY(0); box-shadow: 0 6px 16px rgba(11,102,195,.2); }
  .vx-btn:focus-visible{ box-shadow: 0 0 0 3px var(--focus); }
  .vx-btn .vx-label{ position:relative; z-index:1; }
  .vx-btn .vx-ripple{
    position:absolute; inset:0;
    background: radial-gradient(220px circle at var(--rx,50%) var(--ry,50%), rgba(255,255,255,.55), transparent 60%);
    opacity:0; transform: scale(.9);
    transition: opacity .5s ease, transform .5s ease;
    pointer-events:none;
  }
  .vx-btn:active .vx-ripple{ opacity:1; transform: scale(1); }

  .toast{ position:absolute; bottom:10px; left:12px; background:#0ea5e9; color:#fff; padding:6px 10px; border-radius:8px; font-size:.85rem; box-shadow:0 6px 16px rgba(14,165,233,.25); animation:fadeIn .3s ease both; }

  /* -------- Right: WhatsApp chat -------- */
  .demo-chat-card{ min-height: var(--chat-min-h); }
  .chat-head{
    display:flex; align-items:center; gap:10px;
  }
  .wa-dot{ width:12px; height:12px; border-radius:999px; background:var(--wa); box-shadow:0 0 0 2px #eafaf0; }
  .wa-name{ font-weight:900; }
  .wa-tag{
    margin-left:auto;
    font-size:.72rem; letter-spacing:.12rem; font-weight:900;
    background:#eef2ff; color:var(--brand);
    border:1px solid #dbe7ff; border-radius:999px; padding:6px 8px;
  }

  .chat{ display:flex; flex-direction:column; gap:12px; margin-top:10px; }
  .bubble{
    max-width:86%;
    background:#eef5fb; color:var(--ink);
    padding:12px 16px; border-radius:14px; font-size:.95rem;
    animation:slideIn .25s ease both;
  }
  .bubble.right{
    margin-left:auto; background:var(--brand); color:#fff; border-radius:18px;
  }
  .bubble.typing{ display:inline-flex; align-items:center; gap:6px; }
  .dot{ width:6px; height:6px; background:#94a3b8; border-radius:999px; animation:bounce 1s infinite ease-in-out; }
  .dot:nth-child(2){ animation-delay:.15s; }
  .dot:nth-child(3){ animation-delay:.3s; }

  .quick-row{ display:flex; flex-wrap:wrap; gap:8px; margin-top:4px; }
  .quick{
    border:1px solid #e5ecf7; background:#fff; color:#0f172a;
    border-radius:999px; padding:8px 12px; font-weight:700; font-size:.9rem; cursor:pointer;
    transition: transform .12s ease, box-shadow .12s ease, background .12s ease;
  }
  .quick:hover{ transform:translateY(-1px); box-shadow:0 8px 18px rgba(2,6,23,.06); background:#f9fbff; }

  /* misc */
  .sr-only{ position:absolute; left:-9999px; width:1px; height:1px; overflow:hidden; }

  /* animations */
  @keyframes fadeIn{ from{ opacity:0; transform:translateY(6px) } to{ opacity:1; transform:none } }
  @keyframes slideIn{ from{ opacity:0; transform:translateY(6px) } to{ opacity:1; transform:none } }
  @keyframes bounce{ 0%,100%{ transform:translateY(0) } 50%{ transform:translateY(-3px) } }
  `;

  /* ripple pos for button */
  const btnRef = useRef(null);
  const onSendClick = (e) => {
    const el = btnRef.current;
    if (el) {
      const r = el.getBoundingClientRect();
      el.style.setProperty("--rx", `${e.clientX - r.left}px`);
      el.style.setProperty("--ry", `${e.clientY - r.top}px`);
    }
    onSend();
  };

  return (
    <div
      className="demo-wrap"
      style={{
        "--section-min-h": sectionMinH,
        "--media-h": mediaHeight,
        "--chat-min-h": chatMinH,
      }}
    >
      {/* header */}
      <header className="demo-head">
        <h2 className="demo-title">Interactive demo</h2>
        <p className="demo-tag">
          Preview how a user moves from a Vertex ad into a WhatsApp conversation.
        </p>
      </header>

      <section className="demo-grid">
        {/* ---------- Left: Vertex ad ---------- */}
        <div className="demo-card">
          <div className="demo-card-head">
            <div className="vx-brand">
              <div className="vx-mark">V</div>
              <div>
                <div className="vx-name">Vertex Suite</div>
                <div className="vx-sub">Your Workflow, Fully Automated</div>
              </div>
            </div>
          </div>

          <div
            className="demo-media"
            onMouseMove={onMove}
            onMouseLeave={onLeave}
          >
            <div className="ad-card" ref={cardRef}>
              <div className="ad-row">
                <div className="ad-ico">🛍️</div>
                <div>
                  <h4 className="ad-title">Season Sale</h4>
                  <div className="ad-sub">Click-to-WhatsApp • Sponsored</div>
                </div>
                <div className="ad-pill">Up to 50% OFF</div>
              </div>

              <div className="ad-row" style={{ marginTop: 14 }}>
                <div className="ad-ico">👟</div>
                <div style={{ flex: 1 }}>
                  <div className="ad-title" style={{ fontSize: "1rem" }}>
                    Shoes
                  </div>
                  <div className="ad-sub">Clothing • Popular this week</div>
                </div>
                <div className="ad-pill">₹ 6,499</div>
              </div>
            </div>
          </div>

          <div className="demo-row">
            <div className="demo-caption">
              <div className="demo-caption-title">Click-to-WhatsApp</div>
              <div className="demo-caption-sub">Send users straight into chat</div>
            </div>

            <button
              ref={btnRef}
              className={`vx-btn ${spin.btn ? "spin" : ""}`}
              onClick={onSendClick}
              type="button"
              aria-label="Send message"
            >
              <span className="vx-label">Send Message</span>
              <span className="vx-ripple" aria-hidden="true" />
            </button>
          </div>

          {!!toast && <div className="toast">{toast}</div>}
          <div className="sr-only" aria-live="polite">{liveMsg}</div>
        </div>

        {/* ---------- Right: WhatsApp chat ---------- */}
        <div className="demo-card demo-chat-card">
          <div className="demo-card-head chat-head">
            <span className="wa-dot" />
            <span className="wa-name">WhatsApp Chat</span>
            <span className="wa-tag">META • AI</span>
          </div>

          <div className="chat">
            {chat.map((b, i) =>
              b.typing ? (
                <div key={`t-${b.key}`} className="bubble left typing">
                  <span className="dot" /><span className="dot" /><span className="dot" />
                </div>
              ) : (
                <div
                  key={i}
                  className={`bubble ${b.side}`}
                >
                  {b.text}
                  {/* quick replies appear after first AI message chunk */}
                  {b.ai && i === chat.findIndex(x => x.ai) + 1 && (
                    <div className="quick-row">
                      <button className="quick" onClick={() => onSend("Show shoes")}>Show shoes</button>
                      <button className="quick" onClick={() => onSend("Compare jackets vs bags")}>Compare items</button>
                      <button className="quick" onClick={() => onSend("Talk to agent")}>Talk to agent</button>
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </section>

      <style>{styles}</style>
    </div>
  );
}
